<template>
  <v-off-canvas
    id="case-archived-details-offcanvas"
    :backdrop-static="false"
    offcanvas-class="item-details offcanvas-end"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
        <div class="d-flex align-items-center">
          <v-icon class="text-primary" icon="info" size="xxl" space="me-8" />

          <h5 id="client-details-offcanvas-label" class="offcanvas-title">
            {{ t('clientTour.caseDetails') }}
          </h5>
        </div>
      </off-canvas-header>

      <off-canvas-body class="justify-content-between d-flex flex-column">
        <v-loader v-if="status === 'loading'" />

        <div v-else class="item-details">
          <div class="detail">
            <div class="detail-title d-flex justify-content-between mb-20">
              <div class="fw-medium">{{ t('clientTour.basicInformation') }}</div>
            </div>

            <div class="detail-content">
              <div class="text-sm d-flex py-4">
                <span class="info-title fw-medium">{{ t('general.shared.name') }}: </span>
                <span>{{ clientCase.name }}</span>
              </div>

              <div class="text-sm d-flex py-4">
                <span class="info-title fw-medium">{{ t('general.shared.dates') }}: </span>
                <span>
                  {{ $str.formatDateTime(clientCase.start_date, settings.formatDate) }}
                  -
                  {{ clientCase.end_date ? $str.formatDateTime(clientCase.end_date, settings.formatDate) : '-' }}
                </span>
              </div>

              <div class="text-sm d-flex py-4">
                <span class="info-title fw-medium">{{ t('general.shared.pax') }}: </span>
                <span>{{ clientCase.pax_count }}</span>
              </div>

              <div class="text-sm d-flex py-4">
                <span class="info-title fw-medium">{{ t('clientTour.client') }}: </span>
                <span>{{ clientCase.client?.name }} {{ clientCase.client?.surname }}</span>
              </div>
            </div>
          </div>

          <div class="detail">
            <div class="detail-title fw-medium mb-20">
              {{ t('general.shared.restore') }}
            </div>

            <div class="detail-content">
              <p class="text-sm">{{ t('clientTour.archivedText') }}</p>

              <v-button
                id="btn-restore-case"
                :disabled="disableRestoreBtn"
                class="btn-icon mb-8"
                size="sm"
                variant="primary"
                @click="restoreCase"
              >
                <v-icon icon="unarchive" space="me-12" />
                <template v-if="disableRestoreBtn">{{ t('general.button.restoring') }}</template>
                <template v-else>{{ t('general.button.restore') }}</template>
              </v-button>
            </div>
          </div>

          <div v-if="!!clientCase.deleted_at" class="detail">
            <div class="detail-title fw-medium mb-20">
              {{ t('general.shared.delete') }}
            </div>

            <div class="detail-content">
              <p class="text-sm">{{ t('clientTour.deleteText') }}</p>

              <v-button
                id="btn-delete-case"
                :disabled="disableDeleteBtn"
                class="btn-icon mb-8"
                size="sm"
                variant="danger"
                @click="deleteCase"
              >
                <v-icon icon="trash" space="me-12" />
                <template v-if="disableDeleteBtn">{{ t('general.button.deleting') }}</template>
                <template v-else>{{ t('general.button.delete') }}</template>
              </v-button>
            </div>
          </div>
        </div>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts" setup>
import { ref, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import VOffCanvas from '@/components/vendor/basic/offcanvas/VOffCanvas.vue';
import OffCanvasHeader from '@/components/vendor/basic/offcanvas/OffCanvasHeader.vue';
import OffCanvasBody from '@/components/vendor/basic/offcanvas/OffCanvasBody.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import ResizableContainer from '@/components/resizablecontainer/ResizableContainer.vue';

const { t } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const appStore = useAppStore();
const { agency } = storeToRefs(authStore);
const { settings } = storeToRefs(appStore);

const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal as any;
const $str = instance?.appContext.config.globalProperties.$str as any;

const props = defineProps<{
  clientCase: any;
}>();

const emit = defineEmits(['closed', 'reload-cases']);

const status = ref('loaded');
const disableRestoreBtn = ref(false);
const disableDeleteBtn = ref(false);

const restoreCase = async () => {
  await $modal?.confirm({
    title: t('clientTour.restoreClientTourTitle'),
    text: t('clientTour.restoreClientTourText', { clientTour: props.clientCase.name }),
    leftButtonClasses: 'btn-tertiary',
    confirmButtonText: t('general.button.confirm'),
    confirmButtonCallback: doRestoreCase,
  });
};

const doRestoreCase = async () => {
  if (!agency.value) {
    return;
  }

  try {
    disableRestoreBtn.value = true;
    await api.case.restoreCase(agency.value.slug, props.clientCase.id);
    toast.success(t('general.shared.savedChanges'));
    emit('reload-cases');
    emit('closed');
  } catch (e: any) {
    toast.error(e?.response?.data?.message || t('general.shared.error'));
    console.error(e);
  } finally {
    disableRestoreBtn.value = false;
  }
};

const deleteCase = async () => {
  await $modal?.delete({
    title: t('clientTour.deleteClientTourTitle'),
    text: t('clientTour.deleteClientTourText', { clientTour: props.clientCase.name }),
    deleteButtonText: t('general.button.delete'),
    rightButtonClasses: 'btn-tertiary',
    deleteButtonCallback: doDeleteCase,
  });
};

const doDeleteCase = async () => {
  if (!agency.value) {
    return;
  }

  try {
    disableDeleteBtn.value = true;
    await api.case.forceDeleteCase(agency.value.slug, props.clientCase.id);
    toast.success(t('general.shared.savedChanges'));
    emit('reload-cases');
    emit('closed');
  } catch (e: any) {
    toast.error(e?.response?.data?.message || t('general.shared.error'));
    console.error(e);
  } finally {
    disableDeleteBtn.value = false;
  }
};
</script>
